import { CarFront, CreditCard, ExternalLink, Search, TriangleAlert } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import { rentalSearchUrl } from "../utils/rentalLink";
import type { TravelMode, UiLang, VehicleKind } from "../lib/types";

interface Props {
  mode: TravelMode;
  /** Byen ruta starter i. Leiebilsøket trenger et sted, ikke bare et land. */
  startName: string;
  /** Hva slags kjøretøy. Søket skal svare på det man planlegger, ikke på bil. */
  vehicle?: VehicleKind;
  lang: UiLang;
}

/**
 * Det praktiske appen visste og aldri sa: hvor man kjøper billetten.
 *
 * ── Hva som manglet ───────────────────────────────────────────────────────
 *
 * Planen fortalte hvilket tog som går klokka 08:12 fra Myrdal, og lot det
 * ligge. Den som skal *ta* det toget må kjøpe en billett et sted, og appen
 * hadde ingen mening om hvor. Det samme for bom, for veimeldinger man skal
 * lese før man kjører, og for bilen man ikke har.
 *
 * ── Hvorfor det er få lenker, og hvorfor de er disse ──────────────────────
 *
 * Hver lenke her er *verifisert* å svare, og ingen av dem er valgt av meg blant
 * konkurrenter.
 *
 *   **Entur** selger billetter til all norsk kollektivtrafikk — tog, buss,
 *   båt, ferje. Det er ikke en av flere aktører man kunne pekt på; det er den
 *   statlige billettjenesten, og det er derfor den ene lenka dekker spørsmålet
 *   uten at appen tar et kommersielt valg. Samme kilde tidslinja henter
 *   avgangene fra.
 *
 *   **AutoPASS** er bompengeordningen, og **Statens vegvesen** har
 *   veimeldingene. Begge er myndighetene selv.
 *
 * `vy.no` ble vurdert og *utelatt*: den svarte 403 fra dette nettet, også med
 * en nettleser-UA, så jeg kunne ikke slå fast at lenka virker. Den er
 * åpenbart en ekte tjeneste, og det er nettopp derfor den ikke står her —
 * regelen i denne appen er at en lenke skal være prøvd, ikke antatt. Entur
 * selger de samme billettene.
 *
 * ── Leiebil er et søk ─────────────────────────────────────────────────────
 *
 * Ingen kjede navngis. Appen vet ingenting om pris eller tilgjengelighet, og
 * en sammenligningstjeneste tar provisjon appen ikke ville opplyst om. Se
 * utils/rentalLink.ts.
 */
export function TravelServices({ mode, startName, vehicle = "bil", lang }: Props) {
  const t = dictionaries[lang];
  const byCar = mode === "bil";

  return (
    <div className="card services-card">
      <h3 className="card-title">
        <CreditCard size={14} style={{ verticalAlign: -2, marginRight: 7 }} />
        {t.servicesTitle}
      </h3>

      <ul className="services-list">
        {/*
          Billetter står først uansett reisemåte. En biltur tar ferje, og
          ferjebilletten kjøpes på samme sted som togbilletten.
        */}
        <li className="service">
          <a className="service-link" href="https://entur.no" target="_blank" rel="noreferrer">
            Entur
            <ExternalLink size={11} />
          </a>
          <p className="service-note">{t.servicesTicketsNote}</p>
        </li>

        {byCar && (
          <>
            <li className="service">
              <a
                className="service-link"
                href="https://www.autopass.no"
                target="_blank"
                rel="noreferrer"
              >
                AutoPASS
                <ExternalLink size={11} />
              </a>
              <p className="service-note">{t.servicesTollsNote}</p>
            </li>

            <li className="service">
              <a
                className="service-link"
                href="https://www.vegvesen.no/trafikk/"
                target="_blank"
                rel="noreferrer"
              >
                <TriangleAlert size={11} />
                {t.servicesRoad}
                <ExternalLink size={11} />
              </a>
              <p className="service-note">{t.servicesRoadNote}</p>
            </li>
          </>
        )}

        {/*
          Leiebil er et søk med startbyen i, ikke en lenke til en kjede.
          Vises for alle reisemåter: den som kommer med tog til Bergen og vil
          kjøre den siste dagen har nettopp dette spørsmålet.
        */}
        <li className="service">
          <a
            className="service-link service-link--search"
            href={rentalSearchUrl(startName, lang, vehicle)}
            target="_blank"
            rel="noreferrer"
          >
            <CarFront size={11} />
            {t.servicesRental(startName, t.rentalNoun[vehicle])}
            <Search size={11} />
          </a>
          <p className="service-note">{t.servicesRentalNote}</p>
        </li>
      </ul>

      <p className="services-foot">{t.servicesFoot}</p>
    </div>
  );
}
